import React from 'react';
import { useNavigate } from 'react-router-dom';

export default function DashboardWelcomeBanner({ user, onRefresh, isRefreshing }) {
  const navigate = useNavigate();

  const formattedDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : 'EM';

  return (
    <div className="relative overflow-hidden rounded-3xl border border-outline-variant/20 bg-gradient-to-r from-primary/10 via-surface to-surface-container-low p-6 sm:p-8 shadow-xs">
      {/* Subtle background glow */}
      <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-primary/10 blur-2xl pointer-events-none" />

      <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4 sm:gap-6">
          {/* Avatar / Profile Initials */}
          <div className="relative flex h-16 w-16 sm:h-20 sm:w-20 shrink-0 items-center justify-center rounded-2xl bg-primary text-on-primary font-display font-extrabold text-xl sm:text-2xl shadow-md overflow-hidden">
            {user?.profilePhotoPath ? (
              <img
                src={user.profilePhotoPath}
                alt={user.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <span>{initials}</span>
            )}
            {user?.isManager && (
              <span
                title="Reporting Manager"
                className="absolute bottom-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-white shadow-xs"
              >
                <span className="material-symbols-outlined text-[10px]">star</span>
              </span>
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="rounded-full bg-primary/15 px-2.5 py-0.5 text-[11px] font-bold text-primary">
                {user?.code || 'EMP-STAFF'}
              </span>
              <span className="text-xs font-semibold text-slate-500">
                {user?.department || 'General'}
              </span>
              {user?.companyName && (
                <span className="text-xs text-slate-400">• {user.companyName}</span>
              )}
            </div>

            <h1 className="mt-1 font-display text-2xl sm:text-3xl font-extrabold text-on-surface tracking-tight">
              Welcome back, {user?.name || 'Staff Member'}!
            </h1>

            <p className="mt-1 text-xs sm:text-sm font-medium text-slate-600 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <span className="material-symbols-outlined text-sm text-slate-400">badge</span>
                <span>{user?.designation || 'Associate'}</span>
              </span>
              <span className="text-slate-300">|</span>
              <span className="inline-flex items-center gap-1">
                <span className="material-symbols-outlined text-sm text-slate-400">schedule</span>
                <span>{user?.shiftName || 'General Shift'}</span>
              </span>
            </p>
          </div>
        </div>

        {/* Action Controls & Date */}
        <div className="flex items-center gap-3 self-start sm:self-auto">
          <div className="hidden lg:flex flex-col text-right">
            <span className="text-xs font-bold text-on-surface">{formattedDate}</span>
            <span className="text-[11px] text-slate-400 font-medium">Employee Portal</span>
          </div>

          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            title="Refresh Dashboard"
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-outline-variant/30 bg-surface text-slate-600 shadow-xs hover:bg-surface-container-high transition cursor-pointer disabled:opacity-50"
          >
            <span
              className={`material-symbols-outlined text-lg ${
                isRefreshing ? 'animate-spin text-primary' : ''
              }`}
            >
              refresh
            </span>
          </button>

          <button
            type="button"
            onClick={() => navigate('/self-service/my-profile')}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-xs font-bold text-on-primary shadow-xs hover:bg-primary/90 transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">person</span>
            <span>My Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
}
